import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";

import { api, ApiRequestError } from "./apiClient";

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  /**
   * Sunucudan gelir; istemci hesaplamaz (CLOS-359). Arayüz bu değere bakarak yalnız
   * YÖNLENDİRME yapar — erişim kararı sunucudaki `requireVerifiedEmail` guard'ındadır.
   * İstemcinin bu alanı değiştirmesi hiçbir kapıyı açmaz.
   */
  emailVerified: boolean;
}

/** GET /api/tenants/mine satırı — alan adları listMyMemberships çıktısıyla birebir. */
export interface Membership {
  tenantId: string;
  name: string;
  role: "owner" | "admin" | "member";
}

export interface LoginInput {
  email: string;
  password: string;
}

export interface RegisterInput {
  name: string;
  email: string;
  password: string;
  /**
   * Kabul edilen belge SÜRÜMLERİNİN kimlikleri (CLOS-342). Sunucu bunların yürürlükteki
   * zorunlu metinleri tam kapsadığını doğrular; istemcinin iddiası tek başına yetmez.
   */
  acceptedDocumentIds: string[];
}

interface AuthContextValue {
  user: AuthUser | null;
  memberships: Membership[];
  isLoading: boolean;
  /** Üyelik listesi henüz yükleniyor mu — guard'lar erken karar vermesin diye. */
  membershipsLoading: boolean;
  /** Üyelik sorgusu HATA verdi mi — boş liste ile ağ hatası karışmasın diye. */
  membershipsError: boolean;
  activeTenantId: string | null;
  activeTenant: Membership | null;
  setActiveTenant: (tenantId: string | null) => void;
  login: (input: LoginInput) => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  logout: () => Promise<void>;
}

/**
 * Rota sabitleri — backend path'i değişirse YALNIZ burası güncellenir.
 * Kanonik gateway: /api/session*; ham BetterAuth /api/auth/* rotaları KULLANILMAZ.
 */
export const AUTH_ROUTES = {
  session: "/api/session",
  login: "/api/session/login",
  register: "/api/session/register",
  logout: "/api/session",
  memberships: "/api/tenants/mine",
} as const;

const ACTIVE_TENANT_STORAGE_KEY = "activeTenantId";

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [activeTenantId, setActiveTenantId] = useState<string | null>(() =>
    localStorage.getItem(ACTIVE_TENANT_STORAGE_KEY),
  );

  const sessionQuery = useQuery({
    queryKey: ["session"],
    retry: false,
    queryFn: async (): Promise<{ user: AuthUser | null }> => {
      try {
        return (await api<{ user: AuthUser | null }>(AUTH_ROUTES.session)).data;
      } catch (error) {
        // Oturum yoksa 401 normaldir; hata değil "anonim" durumudur.
        if (error instanceof ApiRequestError && error.code === "UNAUTHORIZED")
          return { user: null };
        throw error;
      }
    },
  });

  const user = sessionQuery.data?.user ?? null;

  const membershipsQuery = useQuery({
    queryKey: ["memberships"],
    enabled: user !== null,
    queryFn: async () => (await api<Membership[]>(AUTH_ROUTES.memberships)).data,
  });

  const memberships = useMemo(() => membershipsQuery.data ?? [], [membershipsQuery.data]);
  const activeTenant =
    memberships.find((membership) => membership.tenantId === activeTenantId) ?? null;

  const setActiveTenant = useCallback((tenantId: string | null) => {
    setActiveTenantId(tenantId);
    if (tenantId) localStorage.setItem(ACTIVE_TENANT_STORAGE_KEY, tenantId);
    else localStorage.removeItem(ACTIVE_TENANT_STORAGE_KEY);
  }, []);

  const refreshSession = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ["session"] });
    await queryClient.invalidateQueries({ queryKey: ["memberships"] });
  }, [queryClient]);

  const login = useCallback(
    async (input: LoginInput) => {
      await api<{ user: AuthUser }>(AUTH_ROUTES.login, {
        method: "POST",
        body: JSON.stringify(input),
      });
      await refreshSession();
    },
    [refreshSession],
  );

  const register = useCallback(
    async (input: RegisterInput) => {
      await api<{ user: AuthUser }>(AUTH_ROUTES.register, {
        method: "POST",
        body: JSON.stringify(input),
      });
      await refreshSession();
    },
    [refreshSession],
  );

  const logout = useCallback(async () => {
    await api<null>(AUTH_ROUTES.logout, { method: "DELETE" });
    setActiveTenant(null);
    queryClient.clear();
    await queryClient.invalidateQueries({ queryKey: ["session"] });
  }, [queryClient, setActiveTenant]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      memberships,
      isLoading: sessionQuery.isPending,
      // Oturum varsa üyelik sorgusu aktiftir; henüz veri gelmediyse yükleniyordur.
      // `isPending` enabled=false iken de true döner, bu yüzden user koşuluyla birlikte.
      membershipsLoading: user !== null && membershipsQuery.isPending,
      membershipsError: membershipsQuery.isError,
      activeTenantId,
      activeTenant,
      setActiveTenant,
      login,
      register,
      logout,
    }),
    [
      user,
      memberships,
      sessionQuery.isPending,
      membershipsQuery.isPending,
      membershipsQuery.isError,
      activeTenantId,
      activeTenant,
      setActiveTenant,
      login,
      register,
      logout,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth, AuthProvider içinde kullanılmalıdır");
  return ctx;
}
